import { useState, useEffect } from 'react';
import { Terminal, FileText, RefreshCw, Clock, Code, GitBranch } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { DeveloperPageLayout } from '@/components/developer/shared/DeveloperPageLayout';
import { SystemStatusCard } from '@/components/developer/widgets/SystemStatusCard';
import { PerformanceMetrics } from '@/components/developer/sections/PerformanceMetrics';
import { ErrorAnalysis } from '@/components/developer/sections/ErrorAnalysis';
import { InfrastructureStatus } from '@/components/developer/sections/InfrastructureStatus';
import { APILimitsMonitor } from '@/components/developer/widgets/APILimitsWidget';
import { LogViewer } from '@/components/developer/shared/LogViewer';
import { MetricBadge } from '@/components/developer/shared/MetricBadge';

// Mock hooks - these would connect to real data
const useSystemHealth = () => ({
  systemHealth: {
    n8n: { status: 'healthy', uptime: 99.98, responseTime: 45 },
    vapi: { status: 'healthy', uptime: 99.95, responseTime: 120 },
    openrouter: { status: 'healthy', uptime: 99.99, responseTime: 89 },
    supabase: { status: 'healthy', uptime: 100, responseTime: 23 }
  }
});

const usePerformanceMetrics = () => ({
  performance: {
    requestsPerMin: 1234,
    avgLatency: 89,
    errorRate: 0.02,
    activeUsers: 342
  }
});

const useErrorTracking = () => ({
  errors: {
    total: 12,
    trend: -15,
    rate: 0.02,
    unique: 8,
    affectedUsers: 3
  }
});

export function DeveloperDashboard() {
  const { systemHealth } = useSystemHealth();
  const { performance } = usePerformanceMetrics();
  const { errors } = useErrorTracking();

  const openTerminal = () => {
    console.log('Opening terminal...');
  };

  const openLogs = () => {
    console.log('Opening logs...');
  };

  const clearAllCaches = () => {
    console.log('Clearing caches...');
  };

  const getLatencyStatus = (latency: number) => {
    if (latency < 100) return 'success';
    if (latency < 200) return 'warning';
    return 'error';
  };

  return (
    <DeveloperPageLayout
      title="Developer Dashboard"
      subtitle="Sistema operacional y métricas técnicas"
      refreshInterval={5000}
      quickActions={[
        {
          icon: <Terminal className="w-4 h-4" />,
          label: "Terminal",
          onClick: openTerminal,
          shortcut: "Ctrl+`"
        },
        {
          icon: <FileText className="w-4 h-4" />,
          label: "Logs",
          onClick: openLogs,
          shortcut: "Ctrl+L"
        },
        {
          icon: <RefreshCw className="w-4 h-4" />,
          label: "Clear Cache",
          onClick: clearAllCaches,
          variant: "danger",
          shortcut: "Ctrl+Shift+C"
        }
      ]}
    >
      {/* System Status Grid */}
      <div className="grid grid-cols-4 gap-2">
        <SystemStatusCard
          service="n8n"
          status={systemHealth.n8n.status}
          uptime="99.98%"
          responseTime="45ms"
        />
        <SystemStatusCard
          service="Vapi"
          status={systemHealth.vapi.status}
          uptime="99.95%"
          responseTime="120ms"
        />
        <SystemStatusCard
          service="OpenRouter"
          status={systemHealth.openrouter.status}
          uptime="99.99%"
          responseTime="89ms"
        />
        <SystemStatusCard
          service="Supabase"
          status={systemHealth.supabase.status}
          uptime="100%"
          responseTime="23ms"
        />
      </div>

      {/* Technical Metrics Tabs */}
      <Tabs defaultValue="performance" className="mt-4">
        <TabsList className="grid grid-cols-5 w-full">
          <TabsTrigger value="performance">Performance</TabsTrigger>
          <TabsTrigger value="errors">Errors</TabsTrigger>
          <TabsTrigger value="infrastructure">Infrastructure</TabsTrigger>
          <TabsTrigger value="api-limits">API Limits</TabsTrigger>
          <TabsTrigger value="logs">Logs</TabsTrigger>
        </TabsList>
        
        <TabsContent value="performance">
          <PerformanceMetrics />
        </TabsContent>
        
        <TabsContent value="errors">
          <ErrorAnalysis />
        </TabsContent>
        
        <TabsContent value="infrastructure">
          <InfrastructureStatus />
        </TabsContent>
        
        <TabsContent value="api-limits">
          <APILimitsMonitor />
        </TabsContent>
        
        <TabsContent value="logs">
          <LogViewer source="system" height={400} />
        </TabsContent>
      </Tabs>

      {/* Quick Stats Bar */}
      <Card className="mt-4">
        <CardContent className="p-3">
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-6">
              <MetricBadge
                label="Requests/min"
                value="1,234"
                trend={12}
              />
              <MetricBadge
                label="Avg Latency"
                value="89ms"
                status={getLatencyStatus(89)}
              />
              <MetricBadge
                label="Error Rate"
                value="0.02%"
                status="success"
              />
              <MetricBadge
                label="Active Users"
                value="342"
              />
            </div>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => window.location.href = '/developer/bots'}
            >
              View All Bots →
            </Button>
          </div>
        </CardContent>
      </Card>
    </DeveloperPageLayout>
  );
}